import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronDown, ChevronRight } from 'lucide-react';
import { useIndustry } from '../context/IndustryContext';
import { getIndustryData } from '../data/industryDataService';
import Walkthrough from '../components/Walkthrough';

const stagePillColors = {
  'Commit': 'bg-green-100 text-green-700',
  'Pipeline': 'bg-blue-100 text-blue-700',
  'Renewal': 'bg-purple-100 text-purple-700',
  'Upside': 'bg-yellow-100 text-yellow-700',
  'Negotiation': 'bg-green-100 text-green-700',
  'Proposal': 'bg-yellow-100 text-yellow-700',
  'Discovery': 'bg-gray-100 text-gray-600',
  'Contract': 'bg-green-100 text-green-700',
};

const statuses = ['All Status', 'At Risk', 'Healthy'];
const valueRanges = ['All Values', '>$3M', '$1M-$3M', '<$1M'];

const walkthroughSteps = [
  { target: '[data-testid="dashboard-header"]', title: 'Opportunities', description: 'Your full pipeline in one table. Every active deal with its stage, risk signals, and value, filterable and searchable. Click any row to drill into the account detail. Available in both Rep and CRO views.' },
  { target: '[data-testid="opportunities-summary"]', title: 'Pipeline Overview', description: 'Your total pipeline value, number of opportunities, and how many are currently flagged at risk, all at a glance.' },
  { target: '[data-testid="opportunities-search"]', title: 'Search & Filter', description: 'Search by account or contact name. Use the dropdowns to filter by pipeline stage, close quarter, risk status, or deal value range.' },
  { target: '[data-testid="opportunities-columns"]', title: 'Account Name', description: 'Shows the company name, its industry, and the assigned rep. Helps you quickly identify which account and who owns it.' },
  { target: '[data-testid="opportunities-columns"]', title: 'Stage', description: 'The current pipeline position, Commit, Pipeline, Renewal, or Upside. Each stage has a distinct color pill for quick scanning.' },
  { target: '[data-testid="opportunities-columns"]', title: 'Risk Signals', description: 'AI-detected alerts like "Champion silent", "Competitor mentioned", or "Budget hold." A dash means no active risk on that deal.' },
  { target: '[data-testid="opportunities-columns"]', title: 'Deal Value', description: 'The annual recurring revenue (ARR) for this opportunity. Formatted with dollar amounts so you can instantly gauge deal size.' },
  { target: '[data-testid="opportunities-columns"]', title: 'Closed/Won Deals', description: 'Historical count of deals won with this account. Higher numbers indicate established relationships and upsell potential.' },
  { target: '[data-testid="opportunity-row-1"]', title: 'Account Details', description: 'Click any row to open the full account view with deal summary, pending actions, activity timeline, and key contacts.' },
];

export default function Opportunities() {
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('All Stages');
  const [quarterFilter, setQuarterFilter] = useState('All Quarters');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [valueFilter, setValueFilter] = useState('All Values');
  const navigate = useNavigate();
  const { selectedIndustry } = useIndustry();
  const { opportunities } = getIndustryData(selectedIndustry);

  const stages = ['All Stages', ...new Set(opportunities.map(o => o.stage))];
  const quarters = ['All Quarters', ...new Set(opportunities.map(o => o.quarter))].sort();

  const filteredOpportunities = opportunities.filter(opp => {
    const matchesSearch = !searchQuery ||
      opp.accountName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      opp.contactName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStage = stageFilter === 'All Stages' || opp.stage === stageFilter;
    const matchesQuarter = quarterFilter === 'All Quarters' || opp.quarter === quarterFilter;
    const matchesStatus = statusFilter === 'All Status' ||
      (statusFilter === 'At Risk' && opp.riskSignal) ||
      (statusFilter === 'Healthy' && !opp.riskSignal);
    const matchesValue = valueFilter === 'All Values' ||
      (valueFilter === '>$3M' && opp.dealValue > 3000000) ||
      (valueFilter === '$1M-$3M' && opp.dealValue >= 1000000 && opp.dealValue <= 3000000) ||
      (valueFilter === '<$1M' && opp.dealValue < 1000000);
    return matchesSearch && matchesStage && matchesQuarter && matchesStatus && matchesValue;
  });

  const totalPipeline = filteredOpportunities.reduce((sum, opp) => sum + opp.dealValue, 0);
  const atRiskCount = filteredOpportunities.filter(opp => opp.riskSignal).length;

  const FilterSelect = ({ value, onChange, options, testId }) => (
    <div className="relative" data-testid={testId}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none flex items-center gap-1.5 px-3 py-2 pr-8 text-sm border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition-colors text-gray-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </select>
      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
    </div>
  );

  const formatDealValue = (value) => {
    return `$${value.toLocaleString()}`;
  };

  return (
    <div className="space-y-6" data-testid="opportunities-page">
      <Walkthrough steps={walkthroughSteps} pageKey="opportunities" />

      {/* Summary Bar */}
      <div className="flex items-center gap-3" data-testid="opportunities-summary">
        <span className="text-sm text-gray-600">
          {filteredOpportunities.length} opportunities &middot; ${totalPipeline >= 1000000 ? `${(totalPipeline / 1000000).toFixed(1)}M` : `${Math.round(totalPipeline / 1000)}K`} pipeline
        </span>
        {atRiskCount > 0 && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-600" data-testid="at-risk-badge">
            {atRiskCount} at risk
          </span>
        )}
      </div>

      {/* Main Card */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm" data-testid="opportunities-card">
        <div className="px-6 py-5 flex items-center justify-between gap-4 flex-wrap">
          <h2 className="text-lg font-bold text-gray-900">Opportunities</h2>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative" data-testid="opportunities-search">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search accounts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-[200px]"
              />
            </div>
            <FilterSelect value={stageFilter} onChange={setStageFilter} options={stages} testId="filter-stages" />
            <FilterSelect value={quarterFilter} onChange={setQuarterFilter} options={quarters} testId="filter-quarters" />
            <FilterSelect value={statusFilter} onChange={setStatusFilter} options={statuses} testId="filter-status" />
            <FilterSelect value={valueFilter} onChange={setValueFilter} options={valueRanges} testId="filter-values" />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead data-testid="opportunities-columns">
              <tr className="border-t border-gray-100 bg-gray-50/50">
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3 w-[30%]">Account Name</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Stage</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Risk Signals</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Deal Value</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Closed/Won Deals</th>
                <th className="w-10 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {filteredOpportunities.length > 0 ? filteredOpportunities.map((opp) => (
                <tr
                  key={opp.id}
                  className="border-t border-gray-100 hover:bg-gray-50/60 transition-colors cursor-pointer"
                  onClick={() => navigate(`/dashboard/opportunities/${opp.id}`)}
                  data-testid={`opportunity-row-${opp.id}`}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg ${opp.avatarColor} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
                        {opp.avatar}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{opp.accountName}</p>
                        <p className="text-xs text-gray-500">{opp.industry} &middot; {opp.contactName}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${stagePillColors[opp.stage] || 'bg-gray-100 text-gray-600'}`}>
                      {opp.stage}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    {opp.riskSignal ? (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700" data-testid={`risk-pill-${opp.id}`}>
                        {opp.riskSignal}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-300">&mdash;</span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm font-bold text-gray-900">{formatDealValue(opp.dealValue)}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm font-semibold text-gray-900">{opp.closedWonDeals}</span>
                  </td>
                  <td className="px-2 py-4">
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-sm text-gray-400" data-testid="no-results">
                    No opportunities match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
